// Weekly posts board (feature 004, restyled in feature 005 "intelly"). Replaces FullCalendar's
// timeGridWeek for the week view with a day-column list: a header row of day pills over 7 columns,
// each a vertical stack of WeekPostCards sorted by time. Day/Month views stay on FullCalendar.
import React from "react";
import {addDays, format, getISOWeek, isSameDay} from "date-fns";
import {DragDropContext, Draggable, Droppable, DropResult} from "react-beautiful-dnd";
import {OverlayTrigger, Popover} from "react-bootstrap";
import {PeerActivityType} from "../../redux/Content/PeerActivity/types";
import {PeerActivityStatus} from "../../redux/Content/PeerActivityStatus/types";
import {convertToMomentLocale} from "../../helpers/functions";
import PopupItem from "../../blocks/Content/PeerActivity/Popup";
import WeekPostCard from "./WeekPostCard";
import {isRescheduleLocked, mapActivityToWeekCardModel} from "./WeekPostCard.helpers";
import VariantGroupCard from "./VariantGroupCard";
import {groupsForDay} from "./VariantGroupCard.helpers";
import {VariantGroupType} from "../../redux/Content/VariantGroup/types";

type Translate = (key: string, defaultValue?: string) => string;

interface Props {
    activities: PeerActivityType[];
    weekStart: Date | null;
    language: string;
    t: Translate;
    onPrev: () => void;
    onNext: () => void;
    onToday: () => void;
    onChangeView: (view: string) => void;
    onToggleIdeas: () => void;
    onEdit: (activity: PeerActivityType) => void;
    onReschedule: (uuid: string, targetDay: Date) => void;
    // Feature 005 US2 — optional so the board renders standalone (US1).
    onAddOnDay?: (day: Date) => void;
    onOpenDay?: (day: Date) => void;
    onDuplicate?: (activity: PeerActivityType) => void;
    onDelete?: (activity: PeerActivityType) => void;
    onRetry?: (activity: PeerActivityType) => void;
    // Native HTML5 drop of an ideas-sidebar card onto a day column → create a post from that idea.
    onIdeaDrop?: (uuid: string, date: Date) => void;
    // Feature 012: planned slots with alternative versions, one card each, and how to open one.
    groups?: VariantGroupType[];
    onOpenGroup?: (group: VariantGroupType) => void;
}

const IDEA_MIME = 'application/x-m9s-idea';

function activityDate(a: PeerActivityType): Date | null {
    const raw = a.activeFrom || a.start;
    if (!raw) return null;
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
}

function startOfDay(d: Date): Date {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
}

const WeekPostsBoard: React.FC<Props> = (props) => {
    const {
        activities, weekStart, language, t,
        onPrev, onNext, onToday, onChangeView, onToggleIdeas, onEdit, onReschedule,
        onAddOnDay, onOpenDay, onDuplicate, onDelete, onRetry, onIdeaDrop, groups, onOpenGroup,
    } = props;
    const locale = convertToMomentLocale(language);
    const now = new Date();
    const todayStart = startOfDay(now);

    // Which day column a native idea-drag is currently over (affordance only).
    const [ideaOverIdx, setIdeaOverIdx] = React.useState<number | null>(null);

    if (!weekStart) {
        return <div className="weekPostsBoard weekPostsBoard--loading"/>;
    }

    const days: Date[] = Array.from({length: 7}, (_, i) => addDays(weekStart, i));
    const weekEnd = days[6];

    const byDay: PeerActivityType[][] = days.map((day) =>
        (activities || [])
            .filter((a) => {
                const d = activityDate(a);
                return d != null && isSameDay(d, day);
            })
            .sort((a, b) => (activityDate(a)!.getTime() - activityDate(b)!.getTime())),
    );

    const meta = days.map((day, i) => {
        const posts = byDay[i];
        const isToday = isSameDay(day, now);
        return {
            day, i, posts, isToday, groups: groupsForDay(groups, day),
            isWeekend: [0, 6].includes(day.getDay()),
            isPast: startOfDay(day).getTime() < todayStart.getTime(),
            hasFail: posts.some((a) => a.status === PeerActivityStatus.STATUS_ERROR),
            nowIdx: isToday ? posts.findIndex((a) => (activityDate(a)?.getTime() ?? 0) >= now.getTime()) : -1,
        };
    });

    const title = `${format(weekStart, "d MMM", {locale})} – ${format(weekEnd, "d MMM yyyy", {locale})}`;
    const weekNo = getISOWeek(weekStart);

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) return;
        if (result.source.droppableId === result.destination.droppableId) return; // same day: no reschedule
        const destIndex = parseInt(result.destination.droppableId, 10);
        if (isNaN(destIndex) || !days[destIndex]) return;
        onReschedule(result.draggableId, days[destIndex]);
    };

    const openDay = (day: Date) => {
        if (onOpenDay) onOpenDay(day);
        else onChangeView("timeGridDay");
    };

    const nowMarker = (
        <div className="weekPostsBoard__now" aria-hidden="true">
            <span className="weekPostsBoard__nowLabel">{t("common:now", "now")} {format(now, "HH:mm")}</span>
            <span className="weekPostsBoard__nowLine"/>
        </div>
    );

    return (
        <div className="weekPostsBoard">
            <div className="weekPostsBoard__toolbar">
                <button type="button" className="weekPostsBoard__icbtn weekPostsBoard__icbtn--pink" title={t("menu:activitiesIdeas")} onClick={onToggleIdeas}>
                    <i className="mdi mdi-diamond-stone"/>
                </button>
                <div className="weekPostsBoard__nav">
                    <button type="button" className="weekPostsBoard__icbtn" aria-label="Previous week" onClick={onPrev}><i className="ri-arrow-left-s-line"/></button>
                    <div className="weekPostsBoard__title">{title}</div>
                    <button type="button" className="weekPostsBoard__icbtn" aria-label="Next week" onClick={onNext}><i className="ri-arrow-right-s-line"/></button>
                </div>
                <div className="weekPostsBoard__seg weekPostsBoard__views">
                    <button type="button" className="weekPostsBoard__segBtn" onClick={onToday}>{t("common:today", "Today")}</button>
                    <button type="button" className="weekPostsBoard__segBtn" onClick={() => onChangeView("timeGridDay")}>{t("common:day", "Day")}</button>
                    <button type="button" className="weekPostsBoard__segBtn weekPostsBoard__segBtn--on" onClick={() => onChangeView("timeGridWeek")}>{t("common:week", "Week")}</button>
                    <button type="button" className="weekPostsBoard__segBtn" onClick={() => onChangeView("dayGridMonth")}>{t("common:month", "Month")}</button>
                </div>
            </div>

            <div className="weekPostsBoard__scroll">
                <div className="weekPostsBoard__heads">
                    <div className="weekPostsBoard__wcell"><span>W</span><b>{weekNo}</b></div>
                    {meta.map((m) => (
                        <button
                            type="button"
                            key={m.i}
                            className={"weekPostsBoard__dayPill"
                                + (m.isToday ? " weekPostsBoard__dayPill--today" : "")
                                + (m.isWeekend ? " weekPostsBoard__dayPill--weekend" : "")}
                            onClick={() => openDay(m.day)}
                        >
                            <span className="weekPostsBoard__dayName">{format(m.day, "EEE", {locale})}</span>
                            <span className="weekPostsBoard__dayNum">{format(m.day, "dd/MM", {locale})}</span>
                            {m.posts.length > 0 &&
                                <span className="weekPostsBoard__count">
                                    {m.posts.length}
                                    {m.hasFail && <span className="weekPostsBoard__countFail" title={t("peerActivityStatus:E", "Failed")}/>}
                                </span>}
                        </button>
                    ))}
                </div>

                <DragDropContext onDragEnd={handleDragEnd}>
                    <div className="weekPostsBoard__grid">
                        <div className="weekPostsBoard__rail" aria-hidden="true"/>
                        {meta.map((m) => (
                            <div
                                className={"weekPostsBoard__col" + (ideaOverIdx === m.i ? " weekPostsBoard__col--ideaover" : "")}
                                key={m.i}
                                // Native idea-drop target (separate event system from the rbd card
                                // reschedule inside, so the two never interfere).
                                onDragOver={(e) => {
                                    if (!onIdeaDrop) return;
                                    e.preventDefault();
                                    e.dataTransfer.dropEffect = 'copy';
                                }}
                                onDragEnter={() => onIdeaDrop && setIdeaOverIdx(m.i)}
                                onDragLeave={(e) => {
                                    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                                    setIdeaOverIdx((idx) => (idx === m.i ? null : idx));
                                }}
                                onDrop={(e) => {
                                    const uuid = e.dataTransfer.getData(IDEA_MIME);
                                    setIdeaOverIdx(null);
                                    if (uuid) {
                                        e.preventDefault();
                                        onIdeaDrop?.(uuid, m.day);
                                    }
                                }}
                            >
                                {m.groups.length > 0 &&
                                    <div className="weekPostsBoard__groups">
                                        {m.groups.map((group) => <VariantGroupCard key={group.uuid} group={group} t={t} onOpen={onOpenGroup}/>)}
                                    </div>}
                                <Droppable droppableId={String(m.i)}>
                                    {(dropProvided, dropSnapshot) => (
                                        <div
                                            ref={dropProvided.innerRef}
                                            {...dropProvided.droppableProps}
                                            className={"weekPostsBoard__list" + (dropSnapshot.isDraggingOver ? " weekPostsBoard__list--over" : "")}
                                        >
                                            {m.posts.length === 0 && m.groups.length === 0 && (
                                                m.isPast
                                                    ? <div className="weekPostsBoard__empty weekPostsBoard__empty--past">{t("peerActivity:noPosts", "No posts")}</div>
                                                    : <div className="weekPostsBoard__empty weekPostsBoard__empty--invite">
                                                        <span className="weekPostsBoard__emptyGlyph">+</span>
                                                        <span>{t("peerActivity:nothingScheduled", "Nothing scheduled")}</span>
                                                        {onAddOnDay &&
                                                            <button type="button" className="btn btn-soft-primary btn-sm" onClick={() => onAddOnDay(m.day)}>
                                                                {t("peerActivity:schedule", "+ Schedule")}
                                                            </button>}
                                                    </div>
                                            )}

                                            {m.posts.map((activity, idx) => {
                                                const start = activityDate(activity);
                                                const model = {
                                                    ...mapActivityToWeekCardModel(activity, start, activity.end ? new Date(activity.end) : null, t, language),
                                                    isCollapsed: false, // list has room: always show the full card
                                                };
                                                const popover = (
                                                    <Popover id={"wkb-" + model.uuid} style={{maxWidth: 400}}>
                                                        <PopupItem item={activity}/>
                                                    </Popover>
                                                );
                                                return (
                                                    <React.Fragment key={model.uuid}>
                                                        {idx === m.nowIdx && nowMarker}
                                                        <Draggable draggableId={model.uuid} index={idx} isDragDisabled={isRescheduleLocked(model.status)}>
                                                            {(dragProvided, dragSnapshot) => (
                                                                <div
                                                                    ref={dragProvided.innerRef}
                                                                    {...dragProvided.draggableProps}
                                                                    {...dragProvided.dragHandleProps}
                                                                    className={"weekPostsBoard__cardWrap"
                                                                        + (dragSnapshot.isDragging ? " weekPostsBoard__cardWrap--dragging" : "")
                                                                        + (isRescheduleLocked(model.status) ? " weekPostsBoard__cardWrap--locked" : "")}
                                                                    onDoubleClick={() => onEdit(activity)}
                                                                >
                                                                    <OverlayTrigger trigger={["click"]} rootClose placement="auto" overlay={popover}>
                                                                        <div>
                                                                            <WeekPostCard
                                                                                model={model}
                                                                                onEdit={() => onEdit(activity)}
                                                                                onDuplicate={onDuplicate ? () => onDuplicate(activity) : undefined}
                                                                                onDelete={onDelete ? () => onDelete(activity) : undefined}
                                                                                onRetry={onRetry ? () => onRetry(activity) : undefined}
                                                                            />
                                                                        </div>
                                                                    </OverlayTrigger>
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    </React.Fragment>
                                                );
                                            })}

                                            {m.isToday && m.posts.length > 0 && m.nowIdx === -1 && nowMarker}

                                            {dropProvided.placeholder}

                                            {m.posts.length > 0 && !m.isPast && onAddOnDay &&
                                                <button type="button" className="weekPostsBoard__add" onClick={() => onAddOnDay(m.day)}>
                                                    {t("peerActivity:addPost", "+ Add post")}
                                                </button>}
                                        </div>
                                    )}
                                </Droppable>
                            </div>
                        ))}
                    </div>
                </DragDropContext>
            </div>
        </div>
    );
};

export default WeekPostsBoard;
